export interface ThemePalette {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  backgroundGradient: string;
}

export interface ThemeDefinition {
  light: ThemePalette;
  dark: ThemePalette;
  /** Optional rotating photo backgrounds, applied instead of the flat gradient. */
  backgroundImages?: string[];
  /** The mode this theme is designed to be seen in; selecting the theme switches to this mode. */
  defaultMode: 'light' | 'dark';
}

export type ThemeName = 'Solitude' | 'Solitude Light';

const SOLITUDE_BACKGROUNDS = [
  '/backgrounds/solitude/IMG_6442-Solitude.webp',
  '/backgrounds/solitude/IMG_6448-Solitude.webp',
  '/backgrounds/solitude/IMG_6449-Solitude.webp',
  '/backgrounds/solitude/IMG_6450-Solitude.webp',
  '/backgrounds/solitude/IMG_6451-Solitude.webp',
  '/backgrounds/solitude/IMG_6459-Solitude.webp',
];

export const themes: Record<ThemeName, ThemeDefinition> = {
  // SOLITUDE: muted graphite/slate, matching the Omarchy "Solitude" desktop theme,
  // with a single warm terracotta accent and rotating personal wallpaper photography.
  Solitude: {
    defaultMode: 'dark',
    dark: {
      primaryColor: '#798186',      // Slate accent
      secondaryColor: '#343d41',    // Deep selection gray
      accentColor: '#de6145',       // Warm terracotta - the one point of warmth
      backgroundColor: '#101315',   // Near-black
      textColor: '#cacccc',
      backgroundGradient: 'linear-gradient(135deg, #101315 0%, #0c0e10 100%)',
    },
    light: {
      primaryColor: '#5d6367',      // Deeper slate for contrast on light bg
      secondaryColor: '#3f4448',
      accentColor: '#c14f36',       // Deeper terracotta
      backgroundColor: '#f4f3f1',   // Warm paper gray
      textColor: '#2a2c2e',
      backgroundGradient: 'linear-gradient(135deg, #f7f6f4 0%, #ece9e5 100%)',
    },
    backgroundImages: SOLITUDE_BACKGROUNDS,
  },

  // SOLITUDE LIGHT: the light sibling of Solitude — same neutral slate +
  // terracotta family and the same rotating wallpaper photography, but with
  // a white scrim instead of a dark one, and a clean white base palette.
  'Solitude Light': {
    defaultMode: 'light',
    light: {
      primaryColor: '#5d6367',      // Same deep slate as Solitude's light palette
      secondaryColor: '#3f4448',
      accentColor: '#c14f36',       // Same terracotta accent, ties it to Solitude
      backgroundColor: '#ffffff',   // Clean white
      textColor: '#1f2123',
      backgroundGradient: 'linear-gradient(135deg, #ffffff 0%, #f5f5f4 100%)',
    },
    // Unused unless a future change reintroduces a manual mode override —
    // kept for shape completeness, identical to Solitude's dark palette.
    dark: {
      primaryColor: '#798186',
      secondaryColor: '#343d41',
      accentColor: '#de6145',
      backgroundColor: '#101315',
      textColor: '#cacccc',
      backgroundGradient: 'linear-gradient(135deg, #101315 0%, #0c0e10 100%)',
    },
    backgroundImages: SOLITUDE_BACKGROUNDS,
  },
};
